import { z } from "zod";

export const operators = ["+", "-", "*", "/", "^", "(", ")"] as const;
const ExprOpType = z.enum(operators);
export type ExprOpType = z.infer<typeof ExprOpType>;
export const isExprOpType = (op: string): op is ExprOpType => ExprOpType.safeParse(op).success;

const BinaryOpType = z.enum(["+", "-", "*", "/", "^"]);
export type BinaryOpType = z.infer<typeof BinaryOpType>;
const isBinaryOp = (op: ExprOpType): op is BinaryOpType => op !== "(" && op !== ")";

export const ExprProduct = z.object({
  type: z.literal("product"),
  product: z.object({ id: z.string(), name: z.string() }),
  period: z.object({ id: z.string(), name: z.string() }),
  artisType: z.string(),
  editable: z.boolean(),
});
export type ExprProduct = z.infer<typeof ExprProduct>;

const ExprOp = z.object({ type: z.literal("operator"), operator: ExprOpType });
export type ExprOp = z.infer<typeof ExprOp>;

const ExprConst = z.object({ type: z.literal("constant"), value: z.number() });
export type ExprConst = z.infer<typeof ExprConst>;

export const ExprToken = z.discriminatedUnion("type", [ExprProduct, ExprConst, ExprOp]);

export type ExprToken = z.infer<typeof ExprToken>;

/** A bracketed sub-expression: has its own base, with an optional chain of ops applied to it. */
export type ExprSubExpr = {
  type: "subexpr";
  base: ExprProduct | ExprConst | ExprSubExpr;
  chain?: ExprBinary;
};

/** Right-recursive chain of operations applied to a parent accumulator. */
export type ExprBinary = {
  type: "expr";
  operator: BinaryOpType;
  left: ExprProduct | ExprConst | ExprSubExpr | ExprBinary;
  right?: ExprProduct | ExprConst | ExprSubExpr | ExprBinary;
};

export const ExprSubExpr: z.ZodType<ExprSubExpr> = z.lazy(() =>
  z.object({
    type: z.literal("subexpr"),
    base: z.union([ExprProduct, ExprConst, ExprSubExpr]),
    chain: ExprBinary.optional(),
  }),
);

export const ExprBinary: z.ZodType<ExprBinary> = z.lazy(() =>
  z.object({
    type: z.literal("expr").default("expr"),
    operator: BinaryOpType,
    left: z.union([ExprProduct, ExprConst, ExprSubExpr, ExprBinary]),
    right: z.union([ExprProduct, ExprConst, ExprSubExpr, ExprBinary]).optional(),
  }),
);

export type Expr = ExprBinary | ExprProduct | ExprConst | ExprSubExpr;
export const Expr = z.union([ExprProduct, ExprConst, ExprSubExpr, ExprBinary]);

const PRECEDENCE: Record<BinaryOpType, number> = {
  "+": 1,
  "-": 1,
  "*": 2,
  "/": 2,
  "^": 3,
};

export const getPrecedence = (op: BinaryOpType): number => PRECEDENCE[op];
export const isRightAssoc = (op: BinaryOpType): boolean => op === "^";

/** Standard fully-binary expression tree, produced by shunting-yard. */
type StdExpr = ExprProduct | ExprConst | { type: "std"; operator: BinaryOpType; left: StdExpr; right: StdExpr };

/** Convert flat token stream to a fully-binary tree using shunting-yard. Returns undefined on invalid input. */
const shuntingYard = (tokens: ExprToken[]): StdExpr | undefined => {
  const output: StdExpr[] = [];
  const ops: ExprOpType[] = [];

  const popOp = (): boolean => {
    const op = ops.pop();
    if (!op || !isBinaryOp(op)) return false;
    const right = output.pop();
    const left = output.pop();
    if (!left || !right) return false;
    output.push({ type: "std", operator: op, left, right });
    return true;
  };

  for (const token of tokens) {
    if (token.type === "product" || token.type === "constant") {
      output.push(token);
      continue;
    }
    const op = token.operator;
    if (op === "(") {
      ops.push(op);
      continue;
    }
    if (op === ")") {
      while (ops.length && ops[ops.length - 1] !== "(") {
        if (!popOp()) return undefined;
      }
      if (ops[ops.length - 1] !== "(") return undefined;
      ops.pop();
      continue;
    }
    const opPrec = PRECEDENCE[op];
    while (ops.length) {
      const top = ops[ops.length - 1];
      if (top === "(" || !isBinaryOp(top)) break;
      const topPrec = PRECEDENCE[top];
      const shouldPop = topPrec > opPrec || (topPrec === opPrec && !isRightAssoc(op));
      if (!shouldPop) break;
      if (!popOp()) return undefined;
    }
    ops.push(op);
  }

  while (ops.length) {
    if (ops[ops.length - 1] === "(") return undefined;
    if (!popOp()) return undefined;
  }

  if (output.length !== 1) return undefined;
  return output[0];
};

type ChainStep = { operator: BinaryOpType; operand: ExprProduct | ExprConst | ExprSubExpr };

const stepsToChain = (steps: ChainStep[]): ExprBinary | undefined => {
  if (steps.length === 0) return undefined;
  const [first, ...rest] = steps;
  return { type: "expr", operator: first.operator, left: first.operand, right: stepsToChain(rest) };
};

const stdToOperand = (tree: StdExpr): ExprProduct | ExprConst | ExprSubExpr => {
  if (tree.type !== "std") return tree;
  const { base, steps } = stdToChain(tree);
  if (steps.length === 0) return base;
  return { type: "subexpr", base, chain: stepsToChain(steps) };
};

const leftmostLeaf = (tree: StdExpr): StdExpr => (tree.type === "std" ? leftmostLeaf(tree.left) : tree);
const leadsWithProduct = (tree: StdExpr): boolean => leftmostLeaf(tree).type === "product";
const negOne = (): ExprConst => ({ type: "constant", value: -1 });

const rerootToProduct = (tree: StdExpr): StdExpr => {
  if (tree.type !== "std") return tree;
  const left = rerootToProduct(tree.left);
  const right = rerootToProduct(tree.right);

  if (!leadsWithProduct(left) && leadsWithProduct(right)) {
    switch (tree.operator) {
      case "+":
      case "*":
        return { type: "std", operator: tree.operator, left: right, right: left };
      case "-":
        // L - R  =  (R * -1) + L
        return {
          type: "std",
          operator: "+",
          left: { type: "std", operator: "*", left: right, right: negOne() },
          right: left,
        };
      case "/":
        // L / R  =  (R ^ -1) * L
        return {
          type: "std",
          operator: "*",
          left: { type: "std", operator: "^", left: right, right: negOne() },
          right: left,
        };
    }
  }

  return { type: "std", operator: tree.operator, left, right };
};

/** Walk a fully-binary tree, extracting the leftmost-leaf as the implicit base and producing a list of chain steps. */
const stdToChain = (tree: StdExpr): { base: ExprProduct | ExprConst | ExprSubExpr; steps: ChainStep[] } => {
  if (tree.type !== "std") return { base: tree, steps: [] };
  const leftResult = stdToChain(tree.left);
  const rightOperand = stdToOperand(tree.right);
  return {
    base: leftResult.base,
    steps: [...leftResult.steps, { operator: tree.operator, operand: rightOperand }],
  };
};

/**
 * Parses the user's token list into:
 *  - base: the leftmost product/constant (used as the chart's primary product)
 *  - expression: the chain of ops to apply to that base (or undefined when there are no ops)
 *
 * Supports brackets and operator precedence via shunting-yard. Returns undefined on malformed input.
 */
export const parseExpression = (
  tokens: ExprToken[],
): { base: ExprProduct | ExprConst | ExprSubExpr; expression?: ExprBinary } | undefined => {
  if (tokens.length === 0) return undefined;
  const std = shuntingYard(tokens);
  if (!std) return undefined;
  const { base, steps } = stdToChain(rerootToProduct(std));
  return { base, expression: stepsToChain(steps) };
};
